/**
 * A supplemental GA4 source, not a replacement for Shopify's collection event.
 * One impression per product/filter context per document, after at least half
 * of its card enters the visible viewport. Hidden and unhydrated cards do not
 * qualify. Uses only the existing top-window Google tag and analytics permission.
 */
if (!customElements.get('th-collection-impressions')) {
  class THCollectionImpressions extends HTMLElement {
    #root;
    #intersection;
    #mutations;
    #observed = new Set();
    #seen = new Set();
    #frame;
    #retry;
    #attempts = 0;

    connectedCallback() {
      this.#root = this.closest('th-collection-products');
      if (!this.#root || !/^G-[A-Z0-9]+$/.test(this.dataset.measurementId || '') ||
          !/^[A-Za-z0-9_-]+$/.test(this.dataset.collectionId || '') ||
          !/^[A-Z]{3}$/.test(this.dataset.currency || '') ||
          !window.IntersectionObserver) return;
      this.#intersection = new IntersectionObserver(this.#schedule, { threshold: [0, 0.5] });
      this.#mutations = new MutationObserver(this.#schedule);
      this.#mutations.observe(this.#root, {
        subtree: true, childList: true, attributes: true,
        attributeFilter: ['hidden', 'data-hydration-state'],
      });
      document.addEventListener('visibilitychange', this.#schedule);
      document.addEventListener('visitorConsentCollected', this.#schedule);
      window.addEventListener('popstate', this.#schedule);
      window.addEventListener('pageshow', this.#schedule);
      if (!window.Shopify?.customerPrivacy && typeof window.Shopify?.loadFeatures === 'function') {
        try {
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], this.#schedule);
        } catch {
          // An unavailable privacy API leaves analytics off; browsing continues.
        }
      }
      this.#schedule();
    }

    disconnectedCallback() {
      this.#intersection?.disconnect();
      this.#mutations?.disconnect();
      this.#observed.clear();
      cancelAnimationFrame(this.#frame);
      clearTimeout(this.#retry);
      this.#frame = null;
      document.removeEventListener('visibilitychange', this.#schedule);
      document.removeEventListener('visitorConsentCollected', this.#schedule);
      window.removeEventListener('popstate', this.#schedule);
      window.removeEventListener('pageshow', this.#schedule);
    }

    #schedule = () => {
      if (!this.isConnected || this.#frame) return;
      this.#frame = requestAnimationFrame(() => {
        this.#frame = null;
        this.#scan();
      });
    };

    #scan() {
      const cards = [...this.#root.querySelectorAll('[data-collection-grid] > li[data-product-id]')];
      const current = new Set(cards);
      for (const card of this.#observed) {
        if (!current.has(card)) { this.#intersection.unobserve(card); this.#observed.delete(card); }
      }
      for (const card of cards) {
        if (!this.#observed.has(card)) { this.#intersection.observe(card); this.#observed.add(card); }
      }
      const privacy = window.Shopify?.customerPrivacy;
      if (typeof window.gtag !== 'function' || typeof privacy?.analyticsProcessingAllowed !== 'function') {
        // Missing APIs fail closed. A bounded retry handles normal deferred loads.
        if (this.#attempts++ < 20) {
          clearTimeout(this.#retry);
          this.#retry = setTimeout(this.#schedule, 500);
        }
        return;
      }
      let allowed = false;
      try { allowed = privacy.analyticsProcessingAllowed() === true; } catch { return; }
      if (!allowed || document.visibilityState !== 'visible' ||
          this.#root.dataset.hydrationState === 'loading') return;

      const params = new URL(window.location.href).searchParams;
      const context = JSON.stringify([
        this.dataset.collectionId,
        params.get('sort_by') || '',
        this.#root.querySelector('[data-collection-pack]')?.value || '',
        this.#root.querySelector('[data-collection-price]')?.value || '',
      ]);
      const visible = cards.filter(card => !card.hidden);
      const products = window.ShopifyAnalytics?.meta?.products;
      if (!Array.isArray(products)) return;
      const items = [];
      const keys = [];
      visible.forEach((card, index) => {
        const key = `${context}:${card.dataset.productId}`;
        if (this.#seen.has(key) || !this.#inViewport(card)) return;
        const product = products.find(entry => String(entry.id) === card.dataset.productId);
        const price = Number(card.dataset.productPrice);
        if (!product || !Number.isFinite(price) || price < 0 || !Array.isArray(product.variants)) return;
        const formId = card.querySelector('input[name="id"]')?.value;
        const variant = product.variants.find(entry => String(entry.id) === formId) ||
          product.variants.find(entry => Number(entry.price) === price);
        if (!variant || !/^\d+$/.test(String(variant.id))) return;
        const title = card.querySelector('.ui-product-card__title')?.textContent.trim();
        if (!title) return;
        items.push({
          product_id: String(product.id),
          product_name: title.slice(0, 100),
          variant_id: String(variant.id),
          price: price / 100,
          index,
        });
        keys.push(key);
      });
      // Google discards ecommerce items arrays on custom events. Flat parameters
      // keep each visible product reportable without adding to the native
      // view_item_list metric. No native event or conversion is emitted here.
      for (let offset = 0; offset < items.length; offset += 1) {
        const item = items[offset];
        try {
          window.gtag('event', 'th_product_impression', {
            send_to: this.dataset.measurementId,
            shopify_event_name: 'th_collection_impression',
            th_impression_source: 'rendered_collection_v1',
            th_pack_filter: this.#root.querySelector('[data-collection-pack]')?.value || 'all',
            th_price_filter: this.#root.querySelector('[data-collection-price]')?.value || 'all',
            currency: this.dataset.currency,
            item_list_id: `th_visible_${this.dataset.collectionId}`,
            item_list_name: this.dataset.collectionTitle,
            th_product_id: item.product_id,
            th_variant_id: item.variant_id,
            th_product_name: item.product_name,
            th_price: item.price,
            th_item_index: item.index,
          });
          this.#seen.add(keys[offset]);
        } catch {
          // Measurement must never block browsing or invoke another pixel path.
        }
      }
    }

    #inViewport(card) {
      if (!card.isConnected || card.closest('[hidden]') || !card.getClientRects().length) return false;
      const style = getComputedStyle(card);
      if (style.visibility === 'hidden' || style.display === 'none') return false;
      const rect = card.getBoundingClientRect();
      const area = rect.width * rect.height;
      if (area <= 0) return false;
      const width = Math.max(0, Math.min(rect.right, innerWidth) - Math.max(rect.left, 0));
      const height = Math.max(0, Math.min(rect.bottom, innerHeight) - Math.max(rect.top, 0));
      return width * height / area >= 0.5;
    }
  }
  customElements.define('th-collection-impressions', THCollectionImpressions);
}
